import React, { useState, useEffect } from 'react';
import GetRequest from 'components/common/GetRequest.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';

// The İç sıvama (Inside lamination) tab body:
//   1. the single material dimension it inherits from the hard_cardboard section it follows
//      (Frank::InsideLaminationMaterialDimension),
//   2. the section's own nav_section form (its wastage + printing question),
//   3. the material (Malzeme) price -- shown only once there is a material dimension to bill against, since
//      inside lamination is priced on that inherited material sheet.
// It re-fetches the dimension when the hard_cardboard solution changes (navSectionsUpdated), is discarded
// (navSectionsReset), or is re-priced (section_result_updated) -- the last is what a hard_cardboard material
// colour switch fires, changing the gray-only inherited sheet count without re-solving. No SVG here, like the
// Profit & Additional Costs section.
const InsideLaminationBody = ({ config, navSection, userQuoteId, sectionIndex, unit }) => {
  const [materialDimension, setMaterialDimension] = useState(null);

  useEffect(() => {
    if (userQuoteId == null) { return undefined; }

    const fetchDimension = () => {
      GetRequest({
        url: `/mcc/${userQuoteId}/inside-lamination-material-dimension/${sectionIndex}?unit=${unit}`,
        callback: (data) => {
          setMaterialDimension(data && data.layout_dimension_id ? {
            label: data.material_dimension,
            layoutDimensionId: data.layout_dimension_id,
            // The gray-only inherited board count; it keys the pricing forms below, so switching a hard_cardboard
            // sub-section between gray and standard colour re-mounts and re-prices them (issue #6194).
            numberOfSheets: data.number_of_sheets,
            width: data.layout_dimension_width,
            height: data.layout_dimension_height,
            printingMethod: data.printing_method,
            acceptedPrintingTypes: data.printing_machine_accepted_printing_types,
          } : null);
        },
      });
    };
    fetchDimension();
    window.addEventListener('navSectionsUpdated', fetchDimension);
    window.addEventListener('navSectionsReset', fetchDimension);
    document.addEventListener('quote_builder.section_result_updated', fetchDimension);
    return () => {
      window.removeEventListener('navSectionsUpdated', fetchDimension);
      window.removeEventListener('navSectionsReset', fetchDimension);
      document.removeEventListener('quote_builder.section_result_updated', fetchDimension);
    };
  }, [userQuoteId, sectionIndex, unit]);

  // A form re-mounts (and so re-prices) only when this key changes -- when the inherited board OR the gray-only
  // sheet count changes. A hard_cardboard colour switch that keeps the same board still changes the count, so the
  // count must ride the key; İç sıvama's own re-price does not change the hard_cardboard set, so the key is stable
  // then and there is no re-price loop.
  const formKey = materialDimension && `${materialDimension.layoutDimensionId}_${materialDimension.numberOfSheets}`;

  // The material sheet the price is billed against -- the inherited sheet. Margins are 0 (it is the whole sheet);
  // num_total / num_of_drawings_in_set are placeholders that do not affect the price, since each İç sıvama
  // calculator computes its own number_of_sheets from the inherited solution's sheet count.
  const materialOptions = materialDimension && {
    layout: {
      layout_dimension_id: materialDimension.layoutDimensionId,
      layout_dimension_width: materialDimension.width,
      layout_dimension_height: materialDimension.height,
      layout_dimension_unit: 'mm',
      layout_dimension_bottom_margin: 0,
      layout_dimension_side_margin: 0,
      layout_dimension_top_margin: 0,
      num_total: 1,
      num_of_drawings_in_set: 1,
    },
  };

  // The printing item bills the SAME inherited sheet, so it reuses the material layout above. It also needs the
  // print size flat (the machine filters read options.layout_dimension_width/height as numbers) and the offset
  // printing method + its accepted printing types (from the endpoint) to filter the printing machines.
  const printingOptions = materialDimension && {
    ...materialOptions,
    layout_dimension_width: parseFloat(materialDimension.width),
    layout_dimension_height: parseFloat(materialDimension.height),
    printing_method: materialDimension.printingMethod,
    printing_machine_accepted_printing_types: materialDimension.acceptedPrintingTypes,
  };

  // The lamination (Sıvama) item bills the SAME inherited sheet. It needs the print size flat (the machine/labor
  // filters read options.layout_dimension_width/height as numbers) and the material_attribute of the İç sıvama
  // material -- a coating -- so its machine/labor list filters and validates against that coating, as the general
  // lamination section does for a cover coating.
  const laminationOptions = materialDimension && {
    ...materialOptions,
    layout_dimension_width: parseFloat(materialDimension.width),
    layout_dimension_height: parseFloat(materialDimension.height),
    material_attribute: 'coating_material_user_price_id',
  };

  return (
    <div data-frank-inside-lamination-body>
      {materialDimension && materialDimension.label && (
        <div style={{ textAlign: 'center', fontSize: '13px', margin: '28px 0 0' }}>
          <span style={{ color: '#888' }}>{t('quote_builder.material_dimensions')}:</span>{' '}
          <strong style={{ color: '#333' }}>{materialDimension.label}</strong>
        </div>
      )}

      {/* The section's own nav_section form -- its wastage and printing question. Rendered on its own (not through
          QuoteBuilderNavSection, which would also pull pricing sub-sections this pricing-only section has none of):
          a memberless synthetic item keeps QuoteBuilderForm happy, and getExtraItems is a no-op because inside
          lamination has no lamination field to fetch window/cellophane extras for. */}
      <div className="quote_builder_container">
        <QuoteBuilderForm
          config={config.nav_section}
          navSection={navSection}
          item={{ uuid: `inside_lamination_${sectionIndex}` }}
          options={{}}
          getExtraItems={() => {}}
          readOnly={window.user_quote_finalised}
        />
      </div>

      {/* The material (Malzeme) price -- only once a material dimension exists to bill against (the tab itself is
          gated on the hard_cardboard solution). Keyed by the dimension so a re-solved hard_cardboard re-seeds and
          re-prices it. Its price persists to this nav section and flows into the quote total.
          columnTitle is "" (this section has no columns -- UserQuoteNavColumn#secondary_identifier): the saved
          selection caches under `${section title}_${columnTitle}`, and getInitialValueFromCache bails when
          columnTitle is undefined, so passing the empty string is what lets the price restore on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={formKey}
            config={config.inside_lamination_material}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_material_${sectionIndex}`}
            options={materialOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}

      {/* The printing (Baskı) price -- the colour, a printing machine (filtered by the inherited sheet's print
          size), and the extra colours. Shown alongside the material price once a material dimension exists, keyed
          by it so a re-solved hard_cardboard re-seeds and re-prices it. Its fire adeti is the section's own (the
          nav_section wastage field), and it bills the same sheet count the material price does. Same drawing item
          and columnTitle="" as the material form, so its price persists and restores on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={formKey}
            config={config.inside_lamination_printing}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_printing_${sectionIndex}`}
            options={printingOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}

      {/* The lamination (Sıvama) price -- the method (None / Manual / Machine) and a lamination machine or manual
          labor, filtered by the selected İç sıvama material (a coating). Shown alongside the other prices once a
          material dimension exists, keyed by it. Its fire adeti is the section's own, and it bills the same sheet
          count the material price does. Same drawing item and columnTitle="" so its price persists on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={formKey}
            config={config.inside_lamination_lamination}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_lamination_${sectionIndex}`}
            options={laminationOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}
    </div>
  );
};

export default InsideLaminationBody;
